import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getCollections, getStats, createCollection, deleteCollection, Collection, Stats } from '../api';

const COLORS = ['#c84b31', '#2d7d6f', '#d4a017', '#1a6491', '#7c3aed', '#0f766e'];

function CreateCollectionModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [color, setColor] = useState('#2d7d6f');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!name.trim()) return;
    setLoading(true);
    await createCollection({ name: name.trim(), description: desc.trim(), color });
    setLoading(false);
    onCreated();
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <h3>Tạo bộ sưu tập mới</h3>
        <div className="form-group">
          <label>Tên bộ sưu tập *</label>
          <input value={name} onChange={e => setName(e.target.value)} placeholder="VD: Tiếng Nhật JLPT" autoFocus />
        </div>
        <div className="form-group">
          <label>Mô tả (tuỳ chọn)</label>
          <textarea value={desc} onChange={e => setDesc(e.target.value)} placeholder="Mô tả ngắn..." />
        </div>
        <div className="form-group">
          <label>Màu sắc</label>
          <div className="color-picker">
            {COLORS.map(c => (
              <button key={c} type="button" className={`color-swatch ${color === c ? 'selected' : ''}`} style={{ background: c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>
        <div className="form-actions">
          <button className="btn btn-ghost" onClick={onClose}>Huỷ</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading || !name.trim()}>
            {loading ? 'Đang tạo...' : 'Tạo bộ sưu tập'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function HomePage() {
  const [collections, setCollections] = useState<Collection[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    try {
      setError('');
      const [c, s] = await Promise.all([getCollections(), getStats()]);
      setCollections(c);
      setStats(s);
    } catch {
      setError('Không thể kết nối backend. Hãy chắc chắn server đang chạy tại cổng 3001.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (e: React.MouseEvent, id: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Xoá bộ sưu tập này? Các bộ từ sẽ không bị xoá nhưng sẽ mất liên kết.')) return;
    await deleteCollection(id);
    load();
  };

  if (loading) return <div className="loading"><div className="spinner" />Đang tải...</div>;

  return (
    <div>
      {error && <div className="alert alert-info" style={{ marginBottom: '1.5rem' }}>⚠️ {error}</div>}

      {stats && (
        <div className="stats-bar stats-bar-5">
          <div className="stat-card">
            <div className="stat-label">Bộ sưu tập</div>
            <div className="stat-value">{stats.total_collections}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Bộ từ</div>
            <div className="stat-value">{stats.total_decks}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Tổng thẻ</div>
            <div className="stat-value">{stats.total_cards}</div>
          </div>
          <div className="stat-card accent">
            <div className="stat-label">Cần ôn hôm nay</div>
            <div className="stat-value">{stats.due_today}</div>
          </div>
          <div className="stat-card jade">
            <div className="stat-label">Đã ôn hôm nay</div>
            <div className="stat-value">{stats.reviewed_today}</div>
          </div>
        </div>
      )}

      <div className="section-header">
        <h2>Bộ sưu tập</h2>
        <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Tạo bộ sưu tập</button>
      </div>

      {collections.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🗂️</div>
          <p>Chưa có bộ sưu tập nào. Hãy tạo bộ sưu tập đầu tiên!</p>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>+ Tạo bộ sưu tập</button>
        </div>
      ) : (
        <div className="collection-grid">
          {collections.map(col => (
            <Link to={`/collection/${col.id}`} key={col.id} className="collection-card" style={{ '--col-color': col.color } as React.CSSProperties}>
              <div className="collection-color-bar" />
              <div className="collection-body">
                <div className="collection-name">{col.name}</div>
                {col.description && <div className="collection-desc">{col.description}</div>}
                <div className="collection-meta">
                  <span>{col.deck_count || 0} bộ từ</span>
                  <span>{col.card_count || 0} thẻ</span>
                  {(col.due_count || 0) > 0 && (
                    <span className="collection-due">{col.due_count} cần ôn</span>
                  )}
                </div>
              </div>
              <div className="collection-actions" onClick={e => e.preventDefault()}>
                <button className="btn btn-danger btn-sm" onClick={e => handleDelete(e, col.id)}>🗑</button>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && <CreateCollectionModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </div>
  );
}
